import { useEffect, useState } from "react";
import { MdCircle, MdClose, MdErrorOutline, MdWarning } from "react-icons/md";
import { ENDPOINTS } from "../../../config/api";
import { PROMPT_CATEGORY_KEYS, PROMPT_CATEGORY_LABELS, STORAGE_KEYS } from "../../../config/constants";
import { apiJson } from "../../../services/httpClient";
import styles from "./PromptEditor.module.css";

const PromptEditor = ({ isOpen, onClose, onSave, cachedData, onReload }) => {
  const [prompts, setPrompts] = useState({});
  const [promptCategories, setPromptCategories] = useState([]);
  const [activeTab, setActiveTab] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [resetConfirm, setResetConfirm] = useState(null);
  const [saveConfirm, setSaveConfirm] = useState(false);
  const [validationError, setValidationError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      if (cachedData) {
        // Use cached data - instant load!
        setPrompts(cachedData.prompts);
        setPromptCategories(cachedData.categories);

        // Set initial active tab to first prompt
        if (cachedData.categories.length > 0 && cachedData.categories[0].prompts.length > 0) {
          setActiveTab(cachedData.categories[0].prompts[0].id);
        }

        setLoading(false);
      } else {
        // Fallback: load from backend (shouldn't happen often)
        loadPrompts();
      }
    }
  }, [isOpen, cachedData]);

  const loadPrompts = async () => {
    setLoading(true);
    setError(null);

    try {
      // First, try to load from localStorage
      const savedPrompts = localStorage.getItem(STORAGE_KEYS.CUSTOM_PROMPTS);
      const customPrompts = savedPrompts ? JSON.parse(savedPrompts) : {};

      const data = await apiJson(ENDPOINTS.PROMPTS, { method: "GET" });

      // Organize prompts by category
      const categorizedPrompts = {};
      for (const key of PROMPT_CATEGORY_KEYS) {
        categorizedPrompts[key] = [];
      }

      const promptsData = {};

      // Fetch full text for each prompt and categorize
      for (const prompt of data.prompts) {
        const detail = await apiJson(`${ENDPOINTS.PROMPTS}/${prompt.key}`, {
          method: "GET",
        });

        // Use custom prompt if available, otherwise use default
        promptsData[prompt.key] = customPrompts[prompt.key] || detail.template;

        // Add to category
        const categoryKey = prompt.category || "other";
        if (categorizedPrompts[categoryKey]) {
          categorizedPrompts[categoryKey].push({
            id: prompt.key,
            label: prompt.name,
          });
        } else {
          categorizedPrompts.other.push({
            id: prompt.key,
            label: prompt.name,
          });
        }
      }

      // Build categories array (only include non-empty categories)
      const categories = PROMPT_CATEGORY_KEYS
        .filter((key) => categorizedPrompts[key].length > 0)
        .map((key) => ({
          name: PROMPT_CATEGORY_LABELS[key],
          prompts: categorizedPrompts[key],
        }));

      setPromptCategories(categories);
      setPrompts(promptsData);

      // Set initial active tab to first prompt
      if (categories.length > 0 && categories[0].prompts.length > 0) {
        setActiveTab(categories[0].prompts[0].id);
      }

      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handlePromptChange = (promptId, value) => {
    setPrompts((prev) => ({ ...prev, [promptId]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    // Double-click confirmation
    if (!saveConfirm) {
      setSaveConfirm(true);
      setTimeout(() => setSaveConfirm(false), 3000);
      return;
    }

    setValidationError(null);

    // Validate prompts with backend before saving
    try {
      const result = await apiJson(ENDPOINTS.PROMPTS_CUSTOM, {
        method: "POST",
        body: JSON.stringify({ prompts }),
      });

      if (!result.success) {
        // Show validation errors inline
        const errorMessages = result.invalid
          .map((inv) => `${inv.key}: ${inv.error}`)
          .join(", ");
        setValidationError(`Validation failed: ${errorMessages}`);
        setSaveConfirm(false);
        return;
      }

      onSave(prompts);

      if (onReload) {
        onReload();
      }

      setHasChanges(false);
      setSaveConfirm(false);

    } catch (error) {
      console.error("Error validating prompts:", error);
      setValidationError(`Error validating prompts: ${error.message}`);
      setSaveConfirm(false);
    }
  };

  const handleReset = async () => {
    // Double-click confirmation
    if (resetConfirm !== "all") {
      setResetConfirm("all");
      setTimeout(() => setResetConfirm(null), 3000);
      return;
    }

    // Clear all custom prompts from localStorage
    localStorage.removeItem(STORAGE_KEYS.CUSTOM_PROMPTS);

    // Reload prompts from API (will now use API defaults)
    if (onReload) {
      onReload(); // Reload cache in parent
    } else {
      await loadPrompts(); // Fallback
    }
    setHasChanges(false);
    setResetConfirm(null);
  };

  const handleResetSingle = async () => {
    // Double-click confirmation
    if (resetConfirm !== activeTab) {
      setResetConfirm(activeTab);
      setTimeout(() => setResetConfirm(null), 3000);
      return;
    }

    try {
      // Fetch the original prompt from API
      const detail = await apiJson(`${ENDPOINTS.PROMPTS}/${activeTab}`, {
        method: "GET",
      });

      // Update prompts state with API version
      setPrompts(prev => ({
        ...prev,
        [activeTab]: detail.template
      }));

      // Update localStorage - remove this specific prompt's custom version
      const savedPrompts = localStorage.getItem(STORAGE_KEYS.CUSTOM_PROMPTS);
      const customPrompts = savedPrompts ? JSON.parse(savedPrompts) : {};
      delete customPrompts[activeTab];
      localStorage.setItem(STORAGE_KEYS.CUSTOM_PROMPTS, JSON.stringify(customPrompts));

      setHasChanges(true);
      setResetConfirm(null);
    } catch (error) {
      setResetConfirm(null);
    }
  };

  const getPlaceholders = (promptText) => {
    if (!promptText) return [];
    const matches = promptText.match(/\{(\w+)\}/g);
    return matches ? [...new Set(matches)] : [];
  };

  const getPlaceholderDescription = (placeholder) => {
    const descriptions = {
      "{text}": "Document content to summarize",
      "{audience}": "Target audience (e.g., students, experts, general)",
      "{style}": "Writing style (e.g., concise, detailed, bullet points)",
      "{feedback}": "User feedback for regeneration",
      "{original_text}": "Original document before summarization",
      "{current_summary}": "Existing summary to improve",
      "{summary}": "Generated summary for post-processing",
      "{instruction}": "Specific editing instruction",
    };
    return descriptions[placeholder] || "Additional context";
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <header className={styles.modalHeader}>
          <h2>Prompt Templates Editor</h2>
          <button
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close"
          >
            <MdClose />
          </button>
        </header>

        {/* Main Content */}
        <main className={styles.modalBody}>
          {loading && (
            <div className={styles.loading}>
              <p>Loading prompts...</p>
            </div>
          )}

          {error && (
            <div className={styles.error}>
              <p><MdErrorOutline /> {error}</p>
              <button className={styles.applyButton} onClick={loadPrompts}>Retry</button>
            </div>
          )}

          {!loading && !error && (
            <div className={styles.editorLayout}>
              {/* Sidebar with prompt categories */}
              <aside className={styles.sidebar}>
                {promptCategories.map((category) => (
                  <div key={category.name} className={styles.category}>
                    <h3>{category.name}</h3>
                    <div className={styles.promptList}>
                      {category.prompts.map((prompt) => (
                        <button
                          key={prompt.id}
                          className={`${styles.promptTab} ${activeTab === prompt.id ? styles.active : ""
                            }`}
                          onClick={() => setActiveTab(prompt.id)}
                        >
                          {prompt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </aside>

              {/* Editor Area */}
              <div className={styles.editor}>
                <div className={styles.editorHeader}>
                  <h3>
                    {promptCategories
                      .flatMap((c) => c.prompts)
                      .find((p) => p.id === activeTab)?.label || activeTab}
                  </h3>
                  <button
                    className={styles.revertButton}
                    onClick={handleResetSingle}
                    style={resetConfirm === activeTab ? { color: '#000' } : {}}
                  >
                    {resetConfirm === activeTab ? "Confirm Reset" : "Reset Default"}
                  </button>
                </div>

                <textarea
                  value={prompts[activeTab] || ""}
                  onChange={(e) => handlePromptChange(activeTab, e.target.value)}
                  className={styles.textarea}
                  placeholder="Loading prompt..."
                  spellCheck={false}
                />

                {/* Placeholders Info */}
                {prompts[activeTab] && (
                  <div className={styles.placeholdersInfo}>
                    <h4>Detected Placeholders:</h4>
                    <div className={styles.placeholdersList}>
                      {getPlaceholders(prompts[activeTab]).length > 0 ? (
                        getPlaceholders(prompts[activeTab]).map((ph) => (
                          <div key={ph} className={styles.placeholderItem}>
                            <code>{ph}</code>
                            <span>{getPlaceholderDescription(ph)}</span>
                          </div>
                        ))
                      ) : (
                        <p className={styles.noPlaceholders}>
                          No placeholders detected
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </main>

        {/* Footer */}
        <footer className={styles.modalFooter}>
          <div className={styles.footerLeft}>
            {hasChanges && (
              <span className={styles.unsavedIndicator}>
                <MdCircle style={{ fontSize: '8px' }} /> Unsaved changes
              </span>
            )}
            {validationError && (
              <span className={styles.unsavedIndicator} style={{ color: "var(--color-error)" }}>
                <MdWarning /> {validationError}
              </span>
            )}
          </div>
          <div className={styles.footerRight}>
            <button
              className={styles.revertButton}
              onClick={handleReset}
              style={resetConfirm === "all" ? { color: '#000' } : {}}
            >
              {resetConfirm === "all" ? "Confirm Reset All" : "Reset All Defaults"}
            </button>
            <button className={styles.revertButton} onClick={onClose} style={{ border: 'none' }}>
              Cancel
            </button>
            <button
              className={styles.applyButton}
              onClick={handleSave}
              disabled={!hasChanges}
              style={saveConfirm ? { color: '#fff' } : {}}
            >
              {saveConfirm ? "Confirm Save" : "Save Changes"}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default PromptEditor;
